// ---------------------------------------------------------------------------
// ScrollTriggeredCta Renderer (editor key: ScrollTriggeredCta)
// ---------------------------------------------------------------------------
// CTA that the storefront theme reveals after the visitor scrolls past a
// threshold. Show/hide behavior lives in theme JS (consumed via the
// `data-ease-trigger-px` and `data-ease-mode` hooks) so the published HTML
// stays JS-free and visible by default — assistive tech sees the CTA
// regardless of the runtime decoration. The CTA href runs through the URL
// safelist; unsupported schemes resolve to `#`. Trigger threshold is
// clamped to a sane range (0-10_000 px) so a malformed editor entry can't
// emit absurd attribute values.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface ScrollTriggeredCtaProps {
  heading?: string;
  body?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  triggerPx?: number | string;
  mode?: "slide-up" | "fade" | "slide-down";
}

const ALLOWED_MODES = new Set(["slide-up", "fade", "slide-down"]);
const MAX_TRIGGER_PX = 10_000;

function normalizeMode(value: unknown): string {
  if (typeof value === "string" && ALLOWED_MODES.has(value)) return value;
  return "slide-up";
}

function normalizeTrigger(value: unknown): number {
  const parsed =
    typeof value === "number" ? value : Number.parseInt(String(value ?? ""), 10);
  if (!Number.isFinite(parsed)) return 600;
  return Math.min(Math.max(Math.round(parsed), 0), MAX_TRIGGER_PX);
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as ScrollTriggeredCtaProps;

  const heading = typeof p.heading === "string" ? p.heading.trim() : "";
  const body = typeof p.body === "string" ? p.body.trim() : "";
  const ctaLabel = typeof p.ctaLabel === "string" ? p.ctaLabel.trim() : "";
  if (!heading && !ctaLabel) {
    return "<!-- scroll-triggered-cta missing content -->";
  }

  const ctaUrl =
    typeof p.ctaUrl === "string" && p.ctaUrl.trim() ? p.ctaUrl.trim() : "#";
  const mode = normalizeMode(p.mode);
  const trigger = normalizeTrigger(p.triggerPx);

  const headingHtml = heading
    ? `\n    <p class="ease-scroll-cta__heading">${text(heading)}</p>`
    : "";
  const bodyHtml = body
    ? `\n    <p class="ease-scroll-cta__body">${text(body)}</p>`
    : "";
  const ctaHtml = ctaLabel
    ? `\n    <a class="ease-scroll-cta__button" href="${url(ctaUrl)}">${text(ctaLabel)}</a>`
    : "";

  return `<aside class="ease-scroll-cta ease-scroll-cta--${attr(mode)}" data-ease-trigger-px="${attr(String(trigger))}" data-ease-mode="${attr(mode)}" role="complementary">
  <div class="ease-scroll-cta__inner">${headingHtml}${bodyHtml}${ctaHtml}
  </div>
</aside>`;
}

registerRenderer("scroll-triggered-cta", { render });
